import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const SettingUpProfile: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/help')}
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('help.backToHelp', 'Back to Help Center')}
        </Button>

        <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 2, display: 'block', mb: 2 }}>
          Getting Started
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
          Setting Up Your Profile
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85, fontSize: '1.1rem' }}>
          Your IndeeClips profile is your identity on the platform. Customize it with a photo, bio, and preferences to make the most of your viewing and creating experience.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Accessing Profile Settings
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Log into your IndeeClips account</li>
          <li>Click your profile icon in the top-right corner</li>
          <li>Select "Profile" or "Settings" from the dropdown menu</li>
          <li>You'll land on your profile editing page</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Profile Picture
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Your profile picture appears next to comments, subscriptions, and your channel.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Supported formats:</strong> JPG, PNG, GIF (non-animated recommended)</li>
          <li><strong>Size limits:</strong> Maximum 2MB file size</li>
          <li><strong>Recommended dimensions:</strong> 200×200 pixels (square)</li>
          <li><strong>Upload:</strong> Click "Change Profile Picture" → Choose file → Crop if needed → Save</li>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          <strong>Tip:</strong> Use a clear, recognizable image. Faces, logos, or avatars work best.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Display Name
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>This is the name shown publicly across IndeeClips</li>
          <li>Can be different from your account email or real name</li>
          <li>Limit: 50 characters</li>
          <li>Change anytime from profile settings</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Bio / About Section
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Add a short description about yourself:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Character limit:</strong> 200 characters</li>
          <li><strong>What to include:</strong> Your interests, content you create, or what you love watching</li>
          <li><strong>Example:</strong> "Tech enthusiast from Mumbai. I upload smartphone reviews in Hindi."</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Language Preference
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Choose your preferred interface language:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Available languages:</strong> English, Hindi (more coming soon)</li>
          <li><strong>What changes:</strong> Menus, buttons, notifications appear in chosen language</li>
          <li><strong>Video content:</strong> Not affected—watch videos in any language regardless of setting</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Theme Selection
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Light Mode:</strong> Bright interface for daytime viewing</li>
          <li><strong>Dark Mode:</strong> Dark background for nighttime or low-light environments</li>
          <li><strong>System Default:</strong> Automatically switches based on your device's theme setting</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Notification Preferences
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Control what notifications you receive:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Email notifications:</strong> New uploads from subscribed channels, replies to comments</li>
          <li><strong>Push notifications (mobile):</strong> Real-time alerts for uploads and interactions</li>
          <li><strong>Recommendation emails:</strong> Weekly digest of suggested videos</li>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Toggle each notification type on/off independently in Settings → Notifications.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Privacy Settings
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Watch history visibility:</strong> Make your watch history private (visible only to you)</li>
          <li><strong>Subscription visibility:</strong> Hide your subscriptions from other users</li>
          <li><strong>Liked videos:</strong> Keep your liked videos list private</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Playback Settings
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Default quality:</strong> Auto (recommended), or set to 480p/720p/1080p</li>
          <li><strong>Autoplay:</strong> Automatically play next suggested video when current one ends</li>
          <li><strong>Data saver mode:</strong> Reduces video quality on mobile data to save bandwidth</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Saving Your Changes
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          After editing any profile section:
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Scroll to the bottom of the settings page</li>
          <li>Click "Save Changes"</li>
          <li>You'll see a confirmation message</li>
          <li>Changes take effect immediately</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Profile vs Channel
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          <strong>Important distinction:</strong> Your profile is for personal account settings. If you want to upload videos, you need to create a separate channel. A channel has its own name, description, and branding—independent of your profile. See "Creating Your Channel" for details.
        </Typography>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Need Assistance?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Questions about profile settings? Email{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default SettingUpProfile;
